export type DirectWriteExifTags = {
  make?: string;
  model?: string;
  lensModel?: string;
  software?: string;
  dateTime?: string;
  dateTimeOriginal?: string;
  dateTimeDigitized?: string;
  iso?: string;
  exposureTime?: string;
  fNumber?: string;
  whiteBalance?: string;
  flash?: string;
  orientation?: string;
  sceneCaptureType?: string;
  exposureProgram?: string;
  focalLength?: string;
  exposureMode?: string;
  meteringMode?: string;
  exposureBiasValue?: string;
  gpsLatitude?: string;
  gpsLatitudeRef?: string;
  gpsLongitude?: string;
  gpsLongitudeRef?: string;
  gpsAltitude?: string;
  gpsAltitudeRef?: string;
  gpsTimeStamp?: string;
  gpsDateStamp?: string;
  gpsImgDirection?: string;
  gpsImgDirectionRef?: string;
};

export type DirectWriteFileMeta = {
  size: number;
  lastModified: number;
  type: string;
  hasExif?: boolean;
  hasGps?: boolean;
  width?: number;
  height?: number;
  exifTags?: DirectWriteExifTags;
};

type ExifSummary = {
  hasExif: boolean;
  hasGps: boolean;
  tags?: DirectWriteExifTags;
};

const EXIF_SLICE_BYTES = 256 * 1024;
const TYPE_SIZES: Record<number, number> = {
  1: 1,
  2: 1,
  3: 2,
  4: 4,
  5: 8,
  7: 1,
  9: 4,
  10: 8,
};

type RationalValue = {
  numerator: number;
  denominator: number;
};

type ExifRawValue = string | number | RationalValue;

type TagDefinition = {
  key: keyof DirectWriteExifTags;
  format?: (value: ExifRawValue) => string;
};

const readAscii = (view: DataView, start: number, length: number) => {
  let out = "";
  for (let i = 0; i < length; i += 1) {
    const code = view.getUint8(start + i);
    out += String.fromCharCode(code);
  }
  return out;
};

const clampTagString = (value: string) => value.replace(/\0+$/, "").trim();

const formatRational = (value: RationalValue) => {
  const { numerator, denominator } = value;
  if (!denominator) return `${numerator}`;
  const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : Math.abs(a));
  const divisor = gcd(numerator, denominator);
  const reducedNum = numerator / divisor;
  const reducedDen = denominator / divisor;
  if (Math.abs(reducedNum) < Math.abs(reducedDen)) {
    return `${reducedNum}/${reducedDen}`;
  }
  const decimal = reducedNum / reducedDen;
  const rounded = decimal % 1 === 0 ? decimal.toFixed(0) : decimal.toFixed(2);
  return rounded.replace(/\.00$/, "");
};

const formatEnumValue = (value: number, map: Record<number, string>) => {
  const label = map[value];
  if (label) {
    return `${label} (${value})`;
  }
  return `${value}`;
};

const formatFlashValue = (value: number) => {
  const fired = (value & 1) === 1;
  const label = fired ? "Fired" : "No flash";
  return `${label} (${value})`;
};

const formatExifValue = (value: ExifRawValue) => {
  if (typeof value === "string") return clampTagString(value);
  if (typeof value === "number") return `${value}`;
  return formatRational(value);
};

const rationalToNumber = (value: RationalValue) => {
  if (!value.denominator) return value.numerator;
  return value.numerator / value.denominator;
};

const formatNumber = (value: number, decimals = 6) => {
  const fixed = value.toFixed(decimals);
  return fixed.replace(/\.?0+$/, "");
};

const formatGpsCoordinate = (parts: RationalValue[], ref?: string) => {
  if (parts.length < 2) return null;
  const deg = rationalToNumber(parts[0]);
  const min = rationalToNumber(parts[1]);
  const sec = parts[2] ? rationalToNumber(parts[2]) : 0;
  if (!Number.isFinite(deg) || !Number.isFinite(min) || !Number.isFinite(sec)) return null;
  const decimal = deg + min / 60 + sec / 3600;
  const sign = ref && (ref.toUpperCase() === "S" || ref.toUpperCase() === "W") ? -1 : 1;
  const value = decimal * sign;
  return formatNumber(value, 6);
};

const formatGpsTime = (parts: RationalValue[]) => {
  if (parts.length < 2) return null;
  const hour = rationalToNumber(parts[0]);
  const minute = rationalToNumber(parts[1]);
  const second = parts[2] ? rationalToNumber(parts[2]) : 0;
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || !Number.isFinite(second)) return null;
  const pad = (num: number) => String(Math.max(0, Math.floor(num))).padStart(2, "0");
  return `${pad(hour)}:${pad(minute)}:${pad(second)}`;
};

const readIfdRationalArray = (
  view: DataView,
  tiffOffset: number,
  entryOffset: number,
  littleEndian: boolean
) => {
  if (entryOffset + 12 > view.byteLength) return null;
  const type = view.getUint16(entryOffset + 2, littleEndian);
  const count = view.getUint32(entryOffset + 4, littleEndian);
  if (!count || (type !== 5 && type !== 10)) return null;
  const size = 8;
  const totalSize = size * count;
  const valueOffset = view.getUint32(entryOffset + 8, littleEndian);
  const valuePos = totalSize <= 4 ? entryOffset + 8 : tiffOffset + valueOffset;
  if (valuePos < 0 || valuePos + totalSize > view.byteLength) return null;
  const values: RationalValue[] = [];
  for (let i = 0; i < count; i += 1) {
    const offset = valuePos + i * 8;
    const numerator =
      type === 10 ? view.getInt32(offset, littleEndian) : view.getUint32(offset, littleEndian);
    const denominator =
      type === 10
        ? view.getInt32(offset + 4, littleEndian)
        : view.getUint32(offset + 4, littleEndian);
    values.push({ numerator, denominator });
  }
  return values;
};

const readIfdValue = (
  view: DataView,
  tiffOffset: number,
  entryOffset: number,
  littleEndian: boolean
): ExifRawValue | null => {
  if (entryOffset + 12 > view.byteLength) return null;
  const type = view.getUint16(entryOffset + 2, littleEndian);
  const count = view.getUint32(entryOffset + 4, littleEndian);
  const size = TYPE_SIZES[type];
  if (!size || count === 0) return null;
  const totalSize = size * count;
  const valueOffset = view.getUint32(entryOffset + 8, littleEndian);
  const valuePos = totalSize <= 4 ? entryOffset + 8 : tiffOffset + valueOffset;
  if (valuePos < 0 || valuePos + totalSize > view.byteLength) return null;

  switch (type) {
    case 1:
    case 7:
      return view.getUint8(valuePos);
    case 2:
      return clampTagString(readAscii(view, valuePos, count));
    case 3:
      return view.getUint16(valuePos, littleEndian);
    case 4:
      return view.getUint32(valuePos, littleEndian);
    case 5:
      return {
        numerator: view.getUint32(valuePos, littleEndian),
        denominator: view.getUint32(valuePos + 4, littleEndian),
      };
    case 9:
      return view.getInt32(valuePos, littleEndian);
    case 10:
      return {
        numerator: view.getInt32(valuePos, littleEndian),
        denominator: view.getInt32(valuePos + 4, littleEndian),
      };
    default:
      return null;
  }
};

const parseIfd = (
  view: DataView,
  tiffOffset: number,
  ifdOffset: number,
  littleEndian: boolean,
  tagMap: Record<number, TagDefinition>
) => {
  const values: Record<string, ExifRawValue> = {};
  let exifOffset: number | undefined;
  let gpsOffset: number | undefined;
  if (ifdOffset + 2 > view.byteLength) {
    return { values, exifOffset, gpsOffset };
  }
  const entries = view.getUint16(ifdOffset, littleEndian);
  for (let i = 0; i < entries; i += 1) {
    const entryOffset = ifdOffset + 2 + i * 12;
    if (entryOffset + 12 > view.byteLength) break;
    const tag = view.getUint16(entryOffset, littleEndian);
    if (tag === 0x8769) {
      const pointer = readIfdValue(view, tiffOffset, entryOffset, littleEndian);
      if (typeof pointer === "number") {
        const offset = tiffOffset + pointer;
        if (offset > 0 && offset < view.byteLength) {
          exifOffset = offset;
        }
      }
      continue;
    }
    if (tag === 0x8825) {
      const pointer = readIfdValue(view, tiffOffset, entryOffset, littleEndian);
      if (typeof pointer === "number") {
        const offset = tiffOffset + pointer;
        if (offset > 0 && offset < view.byteLength) {
          gpsOffset = offset;
        }
      }
      continue;
    }
    const def = tagMap[tag];
    if (!def) continue;
    const raw = readIfdValue(view, tiffOffset, entryOffset, littleEndian);
    if (raw === null || raw === undefined) continue;
    values[def.key] = raw;
  }
  return { values, exifOffset, gpsOffset };
};

const parseGpsIfd = (
  view: DataView,
  tiffOffset: number,
  ifdOffset: number,
  littleEndian: boolean
) => {
  let latitude: RationalValue[] | undefined;
  let latitudeRef: string | undefined;
  let longitude: RationalValue[] | undefined;
  let longitudeRef: string | undefined;
  let altitude: RationalValue | undefined;
  let altitudeRef: number | undefined;
  let timeStamp: RationalValue[] | undefined;
  let dateStamp: string | undefined;
  let imgDirection: RationalValue | undefined;
  let imgDirectionRef: string | undefined;
  if (ifdOffset + 2 > view.byteLength) {
    return {
      altitude,
      altitudeRef,
      latitude,
      latitudeRef,
      longitude,
      longitudeRef,
      timeStamp,
      dateStamp,
      imgDirection,
      imgDirectionRef,
    };
  }
  const entries = view.getUint16(ifdOffset, littleEndian);
  for (let i = 0; i < entries; i += 1) {
    const entryOffset = ifdOffset + 2 + i * 12;
    if (entryOffset + 12 > view.byteLength) break;
    const tag = view.getUint16(entryOffset, littleEndian);
    if (tag === 0x0001) {
      const raw = readIfdValue(view, tiffOffset, entryOffset, littleEndian);
      if (typeof raw === "string") {
        latitudeRef = clampTagString(raw);
      }
    }
    if (tag === 0x0002) {
      const raw = readIfdRationalArray(view, tiffOffset, entryOffset, littleEndian);
      if (raw?.length) {
        latitude = raw;
      }
    }
    if (tag === 0x0003) {
      const raw = readIfdValue(view, tiffOffset, entryOffset, littleEndian);
      if (typeof raw === "string") {
        longitudeRef = clampTagString(raw);
      }
    }
    if (tag === 0x0004) {
      const raw = readIfdRationalArray(view, tiffOffset, entryOffset, littleEndian);
      if (raw?.length) {
        longitude = raw;
      }
    }
    if (tag === 0x0005) {
      const raw = readIfdValue(view, tiffOffset, entryOffset, littleEndian);
      if (typeof raw === "number") {
        altitudeRef = raw;
      }
    }
    if (tag === 0x0006) {
      const raw = readIfdValue(view, tiffOffset, entryOffset, littleEndian);
      if (raw && typeof raw === "object" && "numerator" in raw) {
        altitude = raw;
      }
    }
    if (tag === 0x0007) {
      const raw = readIfdRationalArray(view, tiffOffset, entryOffset, littleEndian);
      if (raw?.length) {
        timeStamp = raw;
      }
    }
    if (tag === 0x0010) {
      const raw = readIfdValue(view, tiffOffset, entryOffset, littleEndian);
      if (raw && typeof raw === "object" && "numerator" in raw) {
        imgDirection = raw;
      }
    }
    if (tag === 0x0011) {
      const raw = readIfdValue(view, tiffOffset, entryOffset, littleEndian);
      if (typeof raw === "string") {
        imgDirectionRef = clampTagString(raw);
      }
    }
    if (tag === 0x001d) {
      const raw = readIfdValue(view, tiffOffset, entryOffset, littleEndian);
      if (typeof raw === "string") {
        dateStamp = clampTagString(raw);
      }
    }
  }
  return {
    altitude,
    altitudeRef,
    latitude,
    latitudeRef,
    longitude,
    longitudeRef,
    timeStamp,
    dateStamp,
    imgDirection,
    imgDirectionRef,
  };
};

const IFD0_TAGS: Record<number, TagDefinition> = {
  0x010f: { key: "make" },
  0x0110: { key: "model" },
  0x0112: {
    key: "orientation",
    format: (value) =>
      formatEnumValue(Number(value), {
        1: "Normal",
        3: "Rotate 180",
        6: "Rotate 90 CW",
        8: "Rotate 270 CW",
      }),
  },
  0x0131: { key: "software" },
  0x0132: { key: "dateTime" },
};

const EXIF_TAGS: Record<number, TagDefinition> = {
  0x9003: { key: "dateTimeOriginal" },
  0x9004: { key: "dateTimeDigitized" },
  0x8827: { key: "iso" },
  0x829a: { key: "exposureTime", format: (value) => `${formatExifValue(value)} s` },
  0x829d: { key: "fNumber", format: (value) => `f/${formatExifValue(value)}` },
  0x9204: { key: "exposureBiasValue", format: (value) => `${formatExifValue(value)} EV` },
  0x9207: {
    key: "meteringMode",
    format: (value) =>
      formatEnumValue(Number(value), {
        0: "Unknown",
        1: "Average",
        2: "Center-weighted average",
        3: "Spot",
        4: "Multi-spot",
        5: "Multi-segment",
        6: "Partial",
        255: "Other",
      }),
  },
  0x9209: { key: "flash", format: (value) => formatFlashValue(Number(value)) },
  0x920a: { key: "focalLength", format: (value) => formatExifValue(value) },
  0xa403: {
    key: "whiteBalance",
    format: (value) =>
      formatEnumValue(Number(value), {
        0: "Auto",
        1: "Manual",
      }),
  },
  0xa402: {
    key: "exposureMode",
    format: (value) =>
      formatEnumValue(Number(value), {
        0: "Auto",
        1: "Manual",
        2: "Auto bracket",
      }),
  },
  0xa406: {
    key: "sceneCaptureType",
    format: (value) =>
      formatEnumValue(Number(value), {
        0: "Standard",
        1: "Landscape",
        2: "Portrait",
        3: "Night",
      }),
  },
  0x8822: {
    key: "exposureProgram",
    format: (value) =>
      formatEnumValue(Number(value), {
        0: "Not defined",
        1: "Manual",
        2: "Program AE",
        3: "Aperture priority",
        4: "Shutter priority",
        5: "Creative",
        6: "Action",
        7: "Portrait",
        8: "Landscape",
      }),
  },
  0xa434: { key: "lensModel" },
};

const parseExifFromJpeg = (buffer: ArrayBuffer, includeTags: boolean): ExifSummary => {
  const view = new DataView(buffer);
  if (view.byteLength < 4) return { hasExif: false, hasGps: false };
  if (view.getUint8(0) !== 0xff || view.getUint8(1) !== 0xd8) {
    return { hasExif: false, hasGps: false };
  }

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) {
      offset += 1;
      continue;
    }
    const marker = view.getUint8(offset + 1);
    if (marker === 0xda || marker === 0xd9) {
      break;
    }
    const size = view.getUint16(offset + 2);
    const segmentStart = offset + 4;
    if (marker === 0xe1 && segmentStart + 6 <= view.byteLength) {
      const header = readAscii(view, segmentStart, 4);
      if (header === "Exif") {
        const tiffOffset = segmentStart + 6;
        if (tiffOffset + 8 > view.byteLength) {
          return { hasExif: true, hasGps: false };
        }
        const endian = readAscii(view, tiffOffset, 2);
        const littleEndian = endian === "II";
        const ifd0Offset = view.getUint32(tiffOffset + 4, littleEndian);
        const ifd0 = tiffOffset + ifd0Offset;
        if (ifd0 + 2 > view.byteLength) {
          return { hasExif: true, hasGps: false };
        }
        const { values, exifOffset, gpsOffset } = parseIfd(
          view,
          tiffOffset,
          ifd0,
          littleEndian,
          includeTags ? IFD0_TAGS : {}
        );
        const hasGps = Boolean(gpsOffset);
        if (!includeTags) {
          return { hasExif: true, hasGps };
        }
        const tags: DirectWriteExifTags = {};
        Object.entries(values).forEach(([key, raw]) => {
          const def = Object.values(IFD0_TAGS).find((entry) => entry.key === key);
          const formatted = def?.format ? def.format(raw) : formatExifValue(raw);
          if (formatted) {
            tags[key as keyof DirectWriteExifTags] = formatted;
          }
        });

        if (exifOffset) {
          const exifData = parseIfd(view, tiffOffset, exifOffset, littleEndian, EXIF_TAGS);
          Object.entries(exifData.values).forEach(([key, raw]) => {
            const def = Object.values(EXIF_TAGS).find((entry) => entry.key === key);
            const formatted = def?.format ? def.format(raw) : formatExifValue(raw);
            if (formatted) {
              tags[key as keyof DirectWriteExifTags] = formatted;
            }
          });
        }

        if (gpsOffset) {
          const gps = parseGpsIfd(view, tiffOffset, gpsOffset, littleEndian);
          if (gps.latitude) {
            const formatted = formatGpsCoordinate(gps.latitude, gps.latitudeRef);
            if (formatted) {
              tags.gpsLatitude = formatted;
            }
            if (gps.latitudeRef) {
              tags.gpsLatitudeRef = gps.latitudeRef;
            }
          }
          if (gps.longitude) {
            const formatted = formatGpsCoordinate(gps.longitude, gps.longitudeRef);
            if (formatted) {
              tags.gpsLongitude = formatted;
            }
            if (gps.longitudeRef) {
              tags.gpsLongitudeRef = gps.longitudeRef;
            }
          }
          if (gps.altitude) {
            const base = gps.altitude.denominator
              ? gps.altitude.numerator / gps.altitude.denominator
              : gps.altitude.numerator;
            const signed = gps.altitudeRef === 1 ? -base : base;
            const label = Number.isFinite(signed)
              ? `${signed % 1 === 0 ? signed.toFixed(0) : signed.toFixed(1)} m`
              : formatRational(gps.altitude);
            tags.gpsAltitude = label;
          }
          if (gps.altitudeRef !== undefined) {
            tags.gpsAltitudeRef = formatEnumValue(gps.altitudeRef, {
              0: "Above sea level",
              1: "Below sea level",
            });
          }
          if (gps.timeStamp) {
            const formatted = formatGpsTime(gps.timeStamp);
            if (formatted) {
              tags.gpsTimeStamp = formatted;
            }
          }
          if (gps.dateStamp) {
            tags.gpsDateStamp = gps.dateStamp;
          }
          if (gps.imgDirection) {
            const direction = rationalToNumber(gps.imgDirection);
            if (Number.isFinite(direction)) {
              tags.gpsImgDirection = formatNumber(direction, 2);
            }
          }
          if (gps.imgDirectionRef) {
            tags.gpsImgDirectionRef = gps.imgDirectionRef;
          }
        }

        return { hasExif: true, hasGps, tags };
      }
    }
    if (!size) {
      break;
    }
    offset += 2 + size;
  }
  return { hasExif: false, hasGps: false };
};

export async function readExifSummary(file: File): Promise<ExifSummary> {
  const mime = file.type || "";
  if (!/jpe?g/i.test(mime) && !/jpe?g$/i.test(file.name)) {
    return { hasExif: false, hasGps: false };
  }
  try {
    const buffer = await file.slice(0, EXIF_SLICE_BYTES).arrayBuffer();
    return parseExifFromJpeg(buffer, false);
  } catch {
    return { hasExif: false, hasGps: false };
  }
}

export async function readExifDetails(file: File): Promise<ExifSummary> {
  const mime = file.type || "";
  if (!/jpe?g/i.test(mime) && !/jpe?g$/i.test(file.name)) {
    return { hasExif: false, hasGps: false };
  }
  try {
    const buffer = await file.slice(0, EXIF_SLICE_BYTES).arrayBuffer();
    return parseExifFromJpeg(buffer, true);
  } catch {
    return { hasExif: false, hasGps: false };
  }
}

export async function readImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  if (typeof createImageBitmap !== "function") {
    return null;
  }
  try {
    const bitmap = await createImageBitmap(file);
    const dims = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return dims;
  } catch {
    return null;
  }
}
